/**
 * Datadog / New Relic / CrowdStrike-style install snippets.
 * Every path fetches the installer or chart from the gateway — no local git clone.
 * After check-in the agent reports cloud + runtime; we never guess that from the website URL.
 */

export type AgentInstallEnvId =
  | 'public'
  | 'laptop'
  | 'windows'
  | 'onprem'
  | 'docker'
  | 'aws_oneclick'
  | 'aws_ssm'
  | 'aws_ec2'
  | 'aws_ecs'
  | 'aws_eks'
  | 'azure'
  | 'gcp'
  | 'k8s';

export type AgentInstallVenueId = 'local' | 'cloud' | 'onprem';

export type AgentInstallCommandOpts = {
  gateway: string;
  token: string;
  label: string;
  language?: 'en' | 'es';
};

export type AgentInstallVenue = {
  id: AgentInstallVenueId;
  label_en: string;
  label_es: string;
  blurb_en: string;
  blurb_es: string;
  envs: AgentInstallEnvId[];
};

/** Three venues operators actually deploy into. */
export const AGENT_INSTALL_VENUES: AgentInstallVenue[] = [
  {
    id: 'local',
    label_en: 'This computer',
    label_es: 'Este equipo',
    blurb_en: 'Mac, Windows, or Linux laptop — one command downloads and checks in.',
    blurb_es: 'Mac, Windows o Linux — un comando descarga y hace check-in.',
    envs: ['laptop', 'windows'],
  },
  {
    id: 'cloud',
    label_en: 'Cloud',
    label_es: 'Nube',
    blurb_en: 'AWS one-click (auto SSM / EKS / provision), or Azure / Google CLI.',
    blurb_es: 'AWS un clic (auto SSM / EKS / provisionar), o CLI de Azure / Google.',
    envs: ['aws_oneclick', 'aws_ssm', 'aws_ec2', 'aws_ecs', 'aws_eks', 'azure', 'gcp'],
  },
  {
    id: 'onprem',
    label_en: 'Company network',
    label_es: 'Red de la empresa',
    blurb_en: 'On-prem Linux, Docker, or Kubernetes behind a firewall — same installer, proxy notes included.',
    blurb_es: 'Linux, Docker o Kubernetes en red privada — mismo instalador, con notas de proxy.',
    envs: ['onprem', 'docker', 'k8s'],
  },
];

/** Flat list (includes public-scan-only for website consoles). */
export const WEBSITE_INSTALL_ENV_ORDER: AgentInstallEnvId[] = [
  'public',
  ...AGENT_INSTALL_VENUES.flatMap((venue) => venue.envs),
];

export const agentInstallEnvLabel = (
  id: AgentInstallEnvId,
  language: 'en' | 'es' = 'en',
): string => {
  if (language === 'es') {
    const es: Record<AgentInstallEnvId, string> = {
      public: 'Solo escaneo público',
      laptop: 'Mac / Linux',
      windows: 'Windows',
      onprem: 'Servidor Linux',
      docker: 'Docker',
      aws_oneclick: 'AWS (un clic)',
      aws_ssm: 'AWS CLI (SSM)',
      aws_ec2: 'AWS EC2',
      aws_ecs: 'AWS ECS',
      aws_eks: 'AWS EKS',
      azure: 'Azure CLI',
      gcp: 'gcloud',
      k8s: 'Kubernetes',
    };
    return es[id];
  }
  const en: Record<AgentInstallEnvId, string> = {
    public: 'Public scan only',
    laptop: 'Mac / Linux',
    windows: 'Windows',
    onprem: 'Linux server',
    docker: 'Docker',
    aws_oneclick: 'AWS (one-click)',
    aws_ssm: 'AWS CLI (SSM)',
    aws_ec2: 'AWS EC2',
    aws_ecs: 'AWS ECS',
    aws_eks: 'AWS EKS',
    azure: 'Azure CLI',
    gcp: 'gcloud',
    k8s: 'Kubernetes',
  };
  return en[id];
};

export const agentInstallEnvBlurb = (
  id: AgentInstallEnvId,
  language: 'en' | 'es' = 'en',
): string => {
  if (language === 'es') {
    const es: Record<AgentInstallEnvId, string> = {
      public: 'Sin agente. El escaneo de homepage ya lee el sitio público.',
      laptop: 'Terminal en esta Mac o PC Linux. Descarga el binario y hace check-in.',
      windows: 'PowerShell descarga install.ps1 y registra el equipo.',
      onprem: 'Servidor Linux en la red de la empresa. Honra HTTPS_PROXY.',
      docker: 'Contenedor en su registro. Pase el token como secreto, no en la imagen.',
      aws_oneclick: 'Un comando: el script detecta perfil/región del AWS CLI local, luego SSM / EKS / EC2. Esta Mac solo corre AWS CLI.',
      aws_ssm: 'Un comando: descarga un script que lista instancias SSM en línea, elige una e instala allí. En esta Mac solo corre AWS CLI — el agente no se instala aquí.',
      aws_ec2: 'User-data o SSH: el script se descarga del gateway en la instancia.',
      aws_ecs: 'CloudFormation se descarga, luego aws cloudformation deploy.',
      aws_eks: 'Helm chart .tgz desde el gateway (sin clonar el repo).',
      azure: 'az vm run-command con el mismo curl|bash.',
      gcp: 'gcloud compute ssh ejecuta el instalador.',
      k8s: 'Cualquier clúster: Helm chart descargado del gateway.',
    };
    return es[id];
  }
  const en: Record<AgentInstallEnvId, string> = {
    public: 'No agent. Homepage scan already reads the public site.',
    laptop: 'Terminal on this Mac or Linux PC. Downloads the binary and checks in.',
    windows: 'PowerShell downloads install.ps1 and enrolls the machine.',
    onprem: 'Linux server on the company network. Honors HTTPS_PROXY.',
    docker: 'Container from your registry. Pass the token as a secret, not in the image.',
    aws_oneclick: 'One command: script detects your local AWS CLI profile/region, then SSM / EKS / EC2. This Mac only runs AWS CLI.',
    aws_ssm: 'One command: downloads a helper that lists your SSM-online instances, picks one, and installs there. This Mac only runs AWS CLI — the agent is not installed here.',
    aws_ec2: 'User-data or SSH: the script is downloaded from the gateway onto the instance.',
    aws_ecs: 'CloudFormation is downloaded, then aws cloudformation deploy.',
    aws_eks: 'Helm chart .tgz from the gateway (no git clone).',
    azure: 'az vm run-command with the same curl|bash.',
    gcp: 'gcloud compute ssh runs the installer.',
    k8s: 'Any cluster: Helm chart downloaded from the gateway.',
  };
  return en[id];
};

export const venueForEnv = (env: AgentInstallEnvId): AgentInstallVenueId | 'public' => {
  if (env === 'public') return 'public';
  const match = AGENT_INSTALL_VENUES.find((venue) => venue.envs.includes(env));
  return match?.id || 'local';
};

export const defaultEnvForVenue = (venue: AgentInstallVenueId): AgentInstallEnvId => {
  if (venue === 'cloud') return 'aws_oneclick';
  if (venue === 'onprem') return 'onprem';
  return 'laptop';
};

/** Outbound allowlist + proxy notes for locked-down / on-prem networks. */
export const agentNetworkGuide = (
  gateway: string,
  language: 'en' | 'es' = 'en',
): { title: string; lines: string[] } => {
  const host = (() => {
    try {
      return new URL(gateway).host || 'nextcompli.com';
    } catch {
      return 'nextcompli.com';
    }
  })();
  if (language === 'es') {
    return {
      title: 'Red / cortafuegos (nube y on‑prem)',
      lines: [
        `Salida HTTPS (TCP 443) hacia ${host} — descarga del binario, install.sh, y cada check-in.`,
        'No se abren puertos de entrada. El agente solo inicia conexiones HTTPS salientes.',
        'Proxy corporativo: export HTTPS_PROXY=https://usuario:clave@proxy:puerto (o HTTP_PROXY).',
        'Inspección TLS / CA propia: instale el certificado raíz en el almacén de confianza del SO.',
        'Sin internet en el host: descargue en un bastión, cópielo (USB/SCP), luego nextcompli-agent checkin …',
        `Prueba: curl -fsS -o /dev/null -w "%{http_code}\\n" https://${host}/`,
      ],
    };
  }
  return {
    title: 'Network / firewall (cloud and on‑prem)',
    lines: [
      `Outbound HTTPS (TCP 443) to ${host} — binary download, install.sh, and every check-in.`,
      'No inbound ports. The agent only opens outbound HTTPS.',
      'Corporate proxy: export HTTPS_PROXY=https://user:pass@proxy:port (or HTTP_PROXY).',
      'TLS inspection / private CA: install the root into the OS trust store (ca-certificates).',
      'Air-gapped host: download on a bastion, copy (USB/SCP), then nextcompli-agent checkin …',
      `Test: curl -fsS -o /dev/null -w "%{http_code}\\n" https://${host}/`,
    ],
  };
};

const shellExports = (gateway: string, token: string, label: string): string[] => ([
  `export NEXTCOMPLI_GATEWAY='${gateway}'`,
  `export NEXTCOMPLI_AGENT_TOKEN='${token}'`,
  `export NEXTCOMPLI_AGENT_LABEL='${label}'`,
]);

/** Install on this machine and start `run` (presence). Not AWS SSM. */
const curlInstall = (label: string): string[] => ([
  `curl -fsSL -H "Authorization: Bearer $NEXTCOMPLI_AGENT_TOKEN" \\`,
  `  "$NEXTCOMPLI_GATEWAY/api/v1/agent/install.sh" | bash -s -- --run --label ${label}`,
]);

/** Which gateway script a copied command will hit (for preflight). */
export function agentInstallScriptPath(env: AgentInstallEnvId): string | null {
  if (env === 'public') return null;
  if (env === 'aws_oneclick') return '/api/v1/agent/install-aws-oneclick.sh';
  if (env === 'aws_ssm') return '/api/v1/agent/install-aws-ssm.sh';
  if (env === 'windows') return '/api/v1/agent/install.ps1';
  if (env === 'aws_ecs') return '/api/v1/agent/deploy/aws/cloudformation-ecs.yaml';
  if (env === 'aws_eks' || env === 'k8s') return '/api/v1/agent/deploy/helm/chart.tgz';
  if (env === 'aws_ec2') return '/api/v1/agent/deploy/aws/ec2-user-data.sh';
  if (env === 'docker') return null;
  return '/api/v1/agent/install.sh';
}

/**
 * Copy/paste blocks must be runnable lines only.
 * Interactive zsh does not treat `#` as a comment by default, and `(...)` triggers NOMATCH.
 * Put operator help in the UI via agentInstallCommandNotes — Datadog/New Relic style one-liners.
 */
const pasteSafe = (lines: string[]): string => (
  lines
    .map((line) => line.replace(/\s+$/, ''))
    .filter((line) => {
      const trimmed = line.trim();
      if (!trimmed) return false;
      if (trimmed.startsWith('#')) return false;
      return true;
    })
    .join('\n')
);

const helmFromGateway = (gateway: string, token: string, label: string): string[] => ([
  'TMP="$(mktemp -d)"',
  `curl -fsSL -H "Authorization: Bearer ${token}" \\`,
  `  "${gateway}/api/v1/agent/deploy/helm/chart.tgz" -o "$TMP/nextcompli-agent.tgz"`,
  'helm upgrade --install nextcompli-agent "$TMP/nextcompli-agent.tgz" \\',
  '  --namespace nextcompli-agent --create-namespace \\',
  `  --set gateway=${gateway} \\`,
  `  --set token=${token} \\`,
  `  --set label=${label} \\`,
  `  --set clusterId=${label} \\`,
  '  --set image.repository=YOUR_REGISTRY/nextcompli-agent \\',
  '  --set image.tag=VERSION',
]);

/** Prefer Mac/Linux vs Windows from the browser that is copying the command. */
export function detectBrowserAgentEnv(): AgentInstallEnvId {
  if (typeof navigator === 'undefined') return 'laptop';
  const ua = String(navigator.userAgent || '');
  const platform = String(
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
    || navigator.platform
    || '',
  );
  if (/Win/i.test(platform) || /Windows/i.test(ua)) return 'windows';
  return 'laptop';
}

export function agentInstallCommandNotes(
  env: AgentInstallEnvId,
  language: 'en' | 'es' = 'en',
): string[] {
  const es = language === 'es';
  if (env === 'aws_oneclick') {
    return es
      ? [
        'Requiere aws CLI y jq en esta Mac. El script detecta perfil/región locales (AWS_PROFILE → perfiles → region del perfil).',
        'Si el perfil es SSO y la sesión caducó, imprime: aws sso login --profile …',
        'Auto: SSM en línea → EKS → o provisiona un EC2. No instala el agente en este Mac.',
      ]
      : [
        'Needs aws CLI and jq on this Mac. The script detects your local profile/region (AWS_PROFILE → profiles → profile region).',
        'If the profile is SSO and the session expired, it prints: aws sso login --profile …',
        'Auto: SSM-online → EKS → or provision EC2. Does not install the agent on this Mac.',
      ];
  }
  if (env === 'aws_ssm') {
    return es
      ? [
        'Requiere aws CLI y jq. El script detecta perfil/región del AWS CLI local (sin fijar nextcompli en el pegado).',
        'Lista instancias SSM en línea e instala allí — no en este Mac.',
      ]
      : [
        'Needs aws CLI and jq. The script detects profile/region from your local AWS CLI (no hardcoded nextcompli in the paste).',
        'Lists SSM-online instances and installs there — not on this Mac.',
      ];
  }
  if (env === 'laptop' || env === 'onprem') {
    return es
      ? ['Instala el agente en ESTA máquina Mac/Linux y deja `run` (presencia + check-in). No es AWS SSM.']
      : ['Installs the agent on THIS Mac/Linux machine and starts `run` (presence + check-in). Not AWS SSM.'];
  }
  if (env === 'windows') {
    return es
      ? ['Ejecute en PowerShell en este PC Windows.']
      : ['Run in PowerShell on this Windows PC.'];
  }
  return [];
}

function buildCommand({
  env,
  gateway,
  token,
  label,
  language = 'en',
}: AgentInstallCommandOpts & { env: AgentInstallEnvId }): string {
  const g = gateway.replace(/\/$/, '');
  const t = String(token || '').trim();
  const site = String(label || 'site-1').trim() || 'site-1';
  const es = language === 'es';

  if (env === 'public') {
    return es
      ? 'No se instala agente. Use «Escanear página de inicio» arriba — lee el sitio público por HTTPS.'
      : 'No agent install. Use “Scan homepage” above — it reads the public site over HTTPS.';
  }
  if (!t || t.includes('…') || t.includes('...')) {
    return es
      ? 'Pulse «Crear token y copiar» en la consola. Pegar un token incompleto da HTTP 401.'
      : 'Click “Create token & copy” in the console. Pasting an incomplete token returns HTTP 401.';
  }
  if (env === 'laptop') {
    return pasteSafe([...shellExports(g, t, site), ...curlInstall(site)]);
  }
  if (env === 'onprem') {
    return pasteSafe([
      ...shellExports(g, t, site),
      'export HTTPS_PROXY="${HTTPS_PROXY:-}"',
      ...curlInstall(site),
    ]);
  }
  if (env === 'windows') {
    return pasteSafe([
      `$env:NEXTCOMPLI_GATEWAY='${g}'`,
      `$env:NEXTCOMPLI_AGENT_TOKEN='${t}'`,
      `$env:NEXTCOMPLI_AGENT_LABEL='${site}'`,
      'irm "$env:NEXTCOMPLI_GATEWAY/api/v1/agent/install.ps1" -Headers @{ Authorization = "Bearer $env:NEXTCOMPLI_AGENT_TOKEN" } | iex',
    ]);
  }
  if (env === 'docker') {
    return pasteSafe([
      'docker run --rm \\',
      `  -e NEXTCOMPLI_GATEWAY=${g} \\`,
      `  -e NEXTCOMPLI_AGENT_TOKEN=${t} \\`,
      `  -e NEXTCOMPLI_AGENT_LABEL=${site} \\`,
      `  -e NEXTCOMPLI_AGENT_CLUSTER_ID=${site} \\`,
      '  -e HTTPS_PROXY -e HTTP_PROXY -e NO_PROXY \\',
      '  YOUR_REGISTRY/nextcompli-agent:VERSION run',
    ]);
  }
  if (env === 'aws_oneclick') {
    // Profile/region are discovered by the script from the local AWS CLI (AWS docs precedence).
    // Do not bake nextcompli/us-east-2 — that breaks other customers' accounts.
    return pasteSafe([
      `export NEXTCOMPLI_GATEWAY='${g}'`,
      `export NEXTCOMPLI_AGENT_TOKEN='${t}'`,
      `export NEXTCOMPLI_AGENT_LABEL='${site}'`,
      'curl -fsSL -H "Authorization: Bearer $NEXTCOMPLI_AGENT_TOKEN" \\',
      `  "$NEXTCOMPLI_GATEWAY/api/v1/agent/install-aws-oneclick.sh" | bash -s -- --label ${site} --yes`,
    ]);
  }
  if (env === 'aws_ssm') {
    return pasteSafe([
      `export NEXTCOMPLI_GATEWAY='${g}'`,
      `export NEXTCOMPLI_AGENT_TOKEN='${t}'`,
      `export NEXTCOMPLI_AGENT_LABEL='${site}'`,
      'curl -fsSL -H "Authorization: Bearer $NEXTCOMPLI_AGENT_TOKEN" \\',
      `  "$NEXTCOMPLI_GATEWAY/api/v1/agent/install-aws-ssm.sh" | bash -s -- --checkin --label ${site}`,
    ]);
  }
  if (env === 'aws_ec2') {
    return pasteSafe([
      ...shellExports(g, t, site),
      `curl -fsSL -H "Authorization: Bearer $NEXTCOMPLI_AGENT_TOKEN" \\`,
      `  "$NEXTCOMPLI_GATEWAY/api/v1/agent/deploy/aws/ec2-user-data.sh" | sudo -E bash`,
    ]);
  }
  if (env === 'aws_ecs') {
    return pasteSafe([
      `curl -fsSL -H "Authorization: Bearer ${t}" \\`,
      `  "${g}/api/v1/agent/deploy/aws/cloudformation-ecs.yaml" -o /tmp/nextcompli-agent-ecs.yaml`,
      'aws cloudformation deploy \\',
      '  --stack-name nextcompli-agent \\',
      '  --template-file /tmp/nextcompli-agent-ecs.yaml \\',
      '  --capabilities CAPABILITY_NAMED_IAM \\',
      '  --parameter-overrides \\',
      '    ClusterName=YOUR_ECS_CLUSTER \\',
      `    GatewayUrl=${g} \\`,
      `    AgentToken=${t} \\`,
      `    SiteLabel=${site} \\`,
      '    ImageUri=YOUR_ACCOUNT.dkr.ecr.REGION.amazonaws.com/nextcompli-agent:VERSION \\',
      '    SubnetIds=subnet-aaa\\\\,subnet-bbb \\',
      '    SecurityGroupIds=sg-xxx',
    ]);
  }
  if (env === 'aws_eks' || env === 'k8s') {
    return pasteSafe(helmFromGateway(g, t, site));
  }
  if (env === 'azure') {
    return pasteSafe([
      'RG=YOUR_RESOURCE_GROUP',
      'VM=YOUR_VM_NAME',
      'az vm run-command invoke \\',
      '  --resource-group "$RG" --name "$VM" \\',
      '  --command-id RunShellScript \\',
      '  --scripts \\',
      `  "export NEXTCOMPLI_GATEWAY='${g}'; export NEXTCOMPLI_AGENT_TOKEN='${t}'; export HTTPS_PROXY=\\"$HTTPS_PROXY\\"; curl -fsSL -H \\"Authorization: Bearer \\$NEXTCOMPLI_AGENT_TOKEN\\" \\"\\$NEXTCOMPLI_GATEWAY/api/v1/agent/install.sh\\" | bash -s -- --checkin --label ${site}"`,
    ]);
  }
  if (env === 'gcp') {
    return pasteSafe([
      'ZONE=us-central1-a',
      'INSTANCE=YOUR_INSTANCE',
      'gcloud compute ssh "$INSTANCE" --zone="$ZONE" --command \\',
      `  "export NEXTCOMPLI_GATEWAY='${g}'; export NEXTCOMPLI_AGENT_TOKEN='${t}'; curl -fsSL -H 'Authorization: Bearer \\$NEXTCOMPLI_AGENT_TOKEN' '\\$NEXTCOMPLI_GATEWAY/api/v1/agent/install.sh' | bash -s -- --checkin --label ${site}"`,
    ]);
  }
  return pasteSafe([...shellExports(g, t, site), ...curlInstall(site)]);
}

export function commandForAgentInstall(
  env: AgentInstallEnvId,
  opts: AgentInstallCommandOpts,
): string {
  return buildCommand({ ...opts, env });
}
